import React, { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Plus, Edit, Trash2, Eye, EyeOff, Users, FolderOpen,
  X, Check, UserCheck, UserX, Link2, Link, ChevronDown, ChevronUp
} from 'lucide-react';

const emptyForm = { name: '', username: '', password: '', phone: '', email: '', is_active: 1 };

export default function ClientsPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('clients');
  const [clients, setClients] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Project access modal
  const [accessClient, setAccessClient] = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [assigningId, setAssigningId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        api.get('/clients'),
        api.get('/projects')
      ]);
      setClients(cRes.data);
      setAllProjects(pRes.data.filter(p => p.status !== 'deleted'));
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditClient(null);
    setForm(emptyForm);
    setShowPassword(false);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditClient(c);
    setForm({ name: c.name, username: c.username, password: '', phone: c.phone || '', email: c.email || '', is_active: c.is_active });
    setShowPassword(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!editClient && !form.password.trim()) return toast.error('Password is required');
    setSaving(true);
    try {
      if (editClient) {
        await api.put(`/clients/${editClient.id}`, {
          name: form.name, phone: form.phone,
          email: form.email || undefined,
          is_active: form.is_active,
          password: form.password || undefined
        });
        toast.success('Client updated');
      } else {
        await api.post('/clients', {
          name: form.name, username: form.username,
          password: form.password, phone: form.phone,
          email: form.email || undefined
        });
        toast.success('Client created');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${deleteTarget.id}`);
      toast.success('Client deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/clients/${c.id}`, { name: c.name, phone: c.phone, is_active: c.is_active ? 0 : 1 });
      toast.success(c.is_active ? 'Client deactivated' : 'Client activated');
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  // Open project access modal
  const openAccess = async (client) => {
    setAccessClient(client);
    setLoadingProjects(true);
    try {
      const r = await api.get(`/clients/${client.id}/projects`);
      setClientProjects(r.data);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const assignProject = async (projectId) => {
    setAssigningId(projectId);
    try {
      await api.post(`/clients/${accessClient.id}/projects`, { project_id: projectId });
      toast.success('Project access granted');
      const r = await api.get(`/clients/${accessClient.id}/projects`);
      setClientProjects(r.data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred');
    } finally {
      setAssigningId(null);
    }
  };

  const revokeProject = async (projectId) => {
    setAssigningId(projectId);
    try {
      await api.delete(`/clients/${accessClient.id}/projects/${projectId}`);
      toast.success('Project access removed');
      const r = await api.get(`/clients/${accessClient.id}/projects`);
      setClientProjects(r.data);
      load();
    } catch {
      toast.error('Remove failed');
    } finally {
      setAssigningId(null);
    }
  };

  const assignedProjectIds = new Set(clientProjects.map(p => p.id));
  const unassignedProjects = allProjects.filter(p => !assignedProjectIds.has(p.id));

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Client Management</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Create clients and grant them access to projects
          </p>
        </div>
        {canWrite && <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> New Client
        </button>}
      </div>

      {/* Clients Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin w-7 h-7 border-4 border-amber-500 border-t-transparent rounded-full" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-14">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No clients yet</p>
            <p className="text-gray-400 text-sm mt-1">Click "+ New Client" to create one</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Username</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Projects</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map(client => (
                  <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                          {client.name.charAt(0)}
                        </div>
                        <span className="font-semibold text-sm text-gray-800">{client.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{client.username}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{client.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {client.email
                        ? <span className="text-blue-600">{client.email}</span>
                        : <span className="text-gray-300 italic text-xs">No email</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-700">
                        {client.project_count}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">projects</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${
                        client.is_active
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {client.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => openAccess(client)}
                          title="Assign projects"
                          className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <FolderOpen className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleActive(client)}
                          title={client.is_active ? 'Deactivate' : 'Activate'}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
                        >
                          {client.is_active
                            ? <UserX className="w-4 h-4" />
                            : <UserCheck className="w-4 h-4 text-green-500" />
                          }
                        </button>
                        <button
                          onClick={() => openEdit(client)}
                          className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-50 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(client)}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create/Edit Modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:480}}>
            <div className="modal-header">
              <h2 className="font-bold text-gray-800">
                {editClient ? 'Edit Client' : 'New Client'}
              </h2>
              <button onClick={() => setShowModal(false)} className="modal-close" type="button">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body space-y-4">
              <div>
                <label className="field-label">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Client name"
                  required
                  className="field-input"
                />
              </div>

              {!editClient && (
                <div>
                  <label className="field-label">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value })}
                    placeholder="Login username"
                    required
                    className="field-input"
                  />
                </div>
              )}

              <div>
                <label className="field-label">
                  Password {!editClient && <span className="text-red-500">*</span>}
                  {editClient && <span className="text-gray-400 font-normal"> (leave blank if no change)</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder={editClient ? 'New password (optional)' : 'Password'}
                    required={!editClient}
                    className="field-input" style={{paddingRight:44}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="field-label">Phone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone number"
                  className="field-input"
                />
              </div>

              <div>
                <label className="field-label">Email Address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="client@example.com"
                  className="field-input"
                />
                <p className="text-xs text-gray-400 mt-1">Required for Forgot Password / Username recovery</p>
              </div>

              {editClient && (
                <div className="flex items-center gap-3">
                  <label className="field-label">Status:</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: form.is_active ? 0 : 1 })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      form.is_active
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-gray-100 text-gray-500 border border-gray-200'
                    }`}
                  >
                    {form.is_active ? <><Check className="w-3 h-3" /> Active</> : 'Inactive'}
                  </button>
                </div>
              )}

              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary" style={{flex:1}}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary" style={{flex:1}}
                >
                  {saving ? 'Saving...' : editClient ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}

      {/* ── Project Access Modal ───────────────────────────────────────────── */}
      {accessClient && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:560}}>
            <div className="modal-header">
              <div>
                <h2 className="font-bold text-gray-800">Manage Project Access</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  <span className="text-blue-600 font-semibold">{accessClient.name}</span> ke liye projects
                </p>
              </div>
              <button onClick={() => setAccessClient(null)} className="modal-close" type="button">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
              {loadingProjects ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-4 border-amber-500 border-t-transparent rounded-full" />
                </div>
              ) : (
                <>
                  {/* Assigned Projects */}
                  <div>
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      ✅ Assigned Projects ({clientProjects.length})
                    </h3>
                    {clientProjects.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">No projects assigned yet</p>
                    ) : (
                      <div className="space-y-2">
                        {clientProjects.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{p.name}</p>
                              {p.project_id && (
                                <p className="text-xs text-gray-400 font-mono">{p.project_id}</p>
                              )}
                            </div>
                            <button
                              onClick={() => revokeProject(p.id)}
                              disabled={assigningId === p.id}
                              className="flex-shrink-0 text-xs px-2.5 py-1.5 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg font-semibold transition-colors disabled:opacity-50"
                            >
                              {assigningId === p.id ? '...' : 'Remove'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Unassigned Projects */}
                  {unassignedProjects.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        📁 Available Projects ({unassignedProjects.length})
                      </h3>
                      <div className="space-y-2">
                        {unassignedProjects.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{p.name}</p>
                              {p.project_id && (
                                <p className="text-xs text-gray-400 font-mono">{p.project_id}</p>
                              )}
                            </div>
                            <button
                              onClick={() => assignProject(p.id)}
                              disabled={assigningId === p.id}
                              className="flex-shrink-0 text-xs px-2.5 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg font-semibold transition-colors disabled:opacity-50"
                            >
                              {assigningId === p.id ? '...' : '+ Add'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => setAccessClient(null)}
                className="btn-secondary" style={{width:"100%"}}
              >
                Done
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ───────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="confirm-box">
            <h2 className="font-bold text-gray-800 mb-2">Delete Client?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Deleting <strong>{deleteTarget.name}</strong> will remove their login and all access.
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="btn-secondary" style={{flex:1}}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
